import { Link, useLocation } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Menu, X, Phone, Mail, ChevronDown, CalendarClock, ExternalLink } from "lucide-react";
import logo from "@/assets/crs-logo.webp";
import { SITE } from "@/lib/site";
import { EmailContactLink } from "@/components/email-contact-link";
import { ensureGhlPreconnect } from "@/lib/ghl-preconnect";
import { SERVICES_BY_CATEGORY, CATEGORY_LABELS, CHECK_BRANDS } from "@/lib/services";
import { SERVICE_CANONICAL_PATHS } from "@/lib/service-routes";
import { GOOGLE_BUSINESS_URL, TRUSTPILOT_AVERAGE_RATING, TRUSTPILOT_BUSINESS_URL } from "@/content/google-reviews";
import { cn } from "@/lib/utils";
import { BookACallButton, BookACallDialog } from "@/components/book-a-call";

import { HomeLeadMagnetStickyBar } from "@/components/home-lead-magnets";
import { TRADE_PROGRAMMES, TRADE_RESOURCES } from "@/content/commercial/trade-menu";
import { FINANCE_NAV_LINKS } from "@/content/finance";
import { useDraftVisible } from "@/lib/draft-pages";
import {
  useHideLeadMagnetSticky,
  useLpNavAnchors,
  useNavMode,
  usePagePhone,
  useShowMobileBottomStickyCta,
  useUtilityBarPhone,
} from "@/lib/nav-config";


export function TopUtilityBar() {
  const phone = useUtilityBarPhone();
  return (
    <div className="hidden md:block bg-primary text-primary-foreground text-sm">
      <div className="container-page flex items-center justify-end gap-6 py-2">
        <a
          href={phone.tel}
          className="inline-flex items-center gap-2 hover:text-accent-soft transition-colors"
        >
          <Phone className="h-4 w-4" aria-hidden />
          <span className="font-medium">Quotation hotline:</span>
          <span>{phone.label}</span>
        </a>
        <EmailContactLink className="inline-flex items-center gap-2 hover:text-accent-soft">
          <span className="inline-flex items-center gap-2">
            <Mail className="h-4 w-4" aria-hidden />
            {SITE.email}
          </span>
        </EmailContactLink>
      </div>
    </div>
  );
}

export function MobileStickyCtaBar({
  position = "header",
}: {
  /** `header` = under logo; `bottom` = fixed to viewport bottom (homepage mobile). */
  position?: "header" | "bottom";
} = {}) {
  const phone = usePagePhone();
  const isBottom = position === "bottom";

  if (isBottom) {
    return (
      <div className="lg:hidden fixed right-4 bottom-[76px] z-[65] flex flex-col gap-2 items-end pointer-events-none">
        <a
          href={phone.tel}
          aria-label={`Call ${phone.label}`}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold shadow-lift active:scale-[0.98] transition-transform"
        >
          <Phone className="h-4 w-4" aria-hidden />
          Call
        </a>
        <BookACallButton
          label="Book a Call"
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground border-transparent px-4 py-2 text-sm font-semibold shadow-lift"
        />
      </div>
    );
  }

  return (
    <div className="lg:hidden bg-primary text-primary-foreground border-b border-primary/20">
      <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center">
        <a
          href={phone.tel}
          aria-label={`Call ${phone.label}`}
          className="w-full sm:flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2.5 text-sm font-semibold shadow-cta active:scale-[0.98] transition-transform min-h-11"
        >
          <Phone className="h-4 w-4" aria-hidden />
          Call
        </a>
        <BookACallButton
          label="Book a Call"
          className="w-full sm:flex-1 justify-center bg-background text-foreground border-transparent px-4 py-2.5 text-sm min-h-11"
        />
      </div>
    </div>
  );

}

function GoogleGLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-label="Google">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18A13.5 13.5 0 0 1 10.98 24c0-1.45.25-2.86.71-4.18v-5.7H4.34A22 22 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

function TrustpilotStar({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-label="Trustpilot">
      <rect width="24" height="24" fill="#00B67A" rx="2" />
      <path
        fill="#fff"
        d="M12 3.5l2.2 5.1 5.55.45-4.22 3.64 1.3 5.41L12 15.27l-4.83 2.83 1.3-5.41-4.22-3.64L9.8 8.6 12 3.5z"
      />
    </svg>
  );
}

function TrustpilotWordmark({ className }: { className?: string }) {
  return (
    <span className={cn("font-semibold tracking-tight", className)}>Trustpilot</span>
  );
}

export function ReviewsBar() {
  return (
    <div className="bg-secondary text-foreground border-b border-border/60">
      <div className="container-page flex items-center justify-center gap-3 sm:gap-8 py-1.5 text-xs sm:text-sm">
        <a
          href={GOOGLE_BUSINESS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 sm:gap-2 shrink-0 hover:opacity-80 transition-opacity"
          aria-label="View CRS Energy on Google, 4.6 out of 5"
        >
          <GoogleGLogo className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="hidden sm:inline font-semibold">Google</span>
          <span className="font-bold text-foreground">4.6</span>
          <span className="inline-flex items-center gap-0.5" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <svg key={i} viewBox="0 0 20 20" className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill="#fbbc04">
                <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L10 14.9l-5.2 2.8 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
              </svg>
            ))}
          </span>
        </a>
        <span className="h-4 w-px bg-border shrink-0" aria-hidden />
        <a
          href={TRUSTPILOT_BUSINESS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 sm:gap-2 shrink-0 hover:opacity-80 transition-opacity"
          aria-label={`View CRS Energy on Trustpilot, ${TRUSTPILOT_AVERAGE_RATING} out of 5`}
        >
          <TrustpilotStar className="h-4 w-4 sm:h-5 sm:w-5" />
          <TrustpilotWordmark className="hidden sm:inline" />
          <span className="font-bold text-foreground">{TRUSTPILOT_AVERAGE_RATING}</span>
          <span className="inline-flex items-center gap-0.5" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <svg key={i} viewBox="0 0 20 20" className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill="#00B67A">
                <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L10 14.9l-5.2 2.8 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
              </svg>
            ))}
          </span>
        </a>
      </div>
    </div>
  );
}




const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/contact", label: "Contact" },
] as const;

function FinanceNavItem({
  label,
  href,
  external,
  onClick,
  className,
}: {
  label: string;
  href: string;
  external: boolean;
  onClick?: () => void;
  className?: string;
}) {
  const content = (
    <>
      {label}
      {external && <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />}
    </>
  );

  if (external || href.includes("#")) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        onClick={onClick}
        className={className}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={href} onClick={onClick} className={className}>
      {content}
    </Link>
  );
}

const ABOUT_LINKS = [
  { to: "/about", label: "About CRS Energy" },
  { to: "/the-team", label: "The Team" },
  { to: "/the-home-check-group", label: "The Home Check Group" },
] as const;

// COMMERCIAL_HUB constant removed with the mega-menu

const LP_SLUG_OVERRIDES = SERVICE_CANONICAL_PATHS;

/** Megamenu spotlight — same destinations as HomeLeadMagnetStickyBar (do not change sticky bar). */
const MEGA_MENU_SPOTLIGHTS = [
  { to: "/solar-pv" as const, label: "Solar PV", variant: "accent" as const },
  { to: "/boiler-upgrade-scheme" as const, label: "Boiler Upgrade Scheme", variant: "primary" as const },
] as const;

function MegaMenu({
  onItemClick,
  onMouseEnter,
  onMouseLeave,
}: {
  onItemClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 top-20 w-[min(64rem,calc(100vw-2rem))] pt-2 z-50 site-fixed-layer"
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="rounded-2xl border bg-popover shadow-lift p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="grid grid-cols-3 gap-6">
          {(Object.keys(SERVICES_BY_CATEGORY) as Array<keyof typeof SERVICES_BY_CATEGORY>).map(
            (category) => (
              <div key={category}>
                <h3 className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">
                  {CATEGORY_LABELS[category]}
                </h3>
                <ul className="space-y-0.5">
                  {SERVICES_BY_CATEGORY[category].map((service) => {
                    const Icon = service.icon;
                    const lpOverride = LP_SLUG_OVERRIDES[service.slug];
                    const linkProps = lpOverride
                      ? ({ to: lpOverride } as const)
                      : ({ to: "/services/$slug", params: { slug: service.slug } } as const);
                    return (
                      <li key={service.slug}>
                        <Link
                          {...linkProps}
                          onClick={onItemClick}
                          className="flex items-start gap-3 rounded-lg p-2 text-sm text-foreground hover:bg-secondary transition-colors group"
                        >
                          <Icon className="h-5 w-5 mt-0.5 text-primary group-hover:text-accent shrink-0" aria-hidden />
                          <span className="font-medium leading-snug">{service.shortName}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          )}
        </div>
        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-3 text-sm">
          <Link
            to="/services"
            onClick={onItemClick}
            className="font-semibold text-primary hover:text-accent transition-colors"
          >
            View all services →
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {MEGA_MENU_SPOTLIGHTS.map((spotlight) => (
              <Link
                key={spotlight.to}
                to={spotlight.to}
                onClick={onItemClick}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-4 py-2 font-semibold transition-colors",
                  spotlight.variant === "accent"
                    ? "bg-accent text-accent-foreground hover:bg-accent/90"
                    : "bg-primary text-primary-foreground hover:bg-primary/90",
                )}
              >
                {spotlight.label} →
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TradeMegaMenu({
  onItemClick,
  onMouseEnter,
  onMouseLeave,
}: {
  onItemClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 top-20 w-[min(56rem,calc(100vw-2rem))] pt-2 z-50 site-fixed-layer"
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="rounded-2xl border bg-popover shadow-lift p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">
              Programmes
            </h3>
            <ul className="space-y-0.5">
              {TRADE_PROGRAMMES.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    onClick={onItemClick}
                    className="block rounded-lg p-2 text-sm font-medium text-foreground hover:bg-secondary hover:text-accent transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">
              Resources
            </h3>
            <ul className="space-y-1.5">
              {TRADE_RESOURCES.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    onClick={onItemClick}
                    className="block rounded-lg p-2 text-foreground hover:bg-secondary group"
                  >
                    <span className="block text-sm font-medium group-hover:text-accent">
                      {l.label}
                    </span>
                    {l.blurb && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {l.blurb}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t text-sm">
          <Link
            to="/for-trade"
            onClick={onItemClick}
            className="font-semibold text-primary hover:text-accent transition-colors"
          >
            Partnerships hub →
          </Link>
        </div>
      </div>
    </div>
  );
}



export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileClosing, setMobileClosing] = useState(false);
  const mobileCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [mobileTradeOpen, setMobileTradeOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [mobileFinanceOpen, setMobileFinanceOpen] = useState(false);
  const draftVisible = useDraftVisible();
  const financeNavLinks = FINANCE_NAV_LINKS.filter(
    (l) => draftVisible || !("draftOnly" in l && l.draftOnly),
  );

  const servicesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aboutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tradeCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const financeCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navMode = useNavMode();
  const isLp = navMode === "funnel";
  const isHome = location.pathname === "/";
  const showBottomStickyBar = useShowMobileBottomStickyCta();
  const showTradeMenu = location.pathname.startsWith("/content");
  const lpNavAnchors = useLpNavAnchors();
  const hideLeadMagnetSticky = useHideLeadMagnetSticky();
  const showLeadMagnetSticky = !hideLeadMagnetSticky;
  const ctaPhone = usePagePhone();

  const closeMobileDrawer = useCallback(() => {
    if (!mobileOpen || mobileClosing) return;
    setMobileClosing(true);
    if (mobileCloseTimer.current) clearTimeout(mobileCloseTimer.current);
    mobileCloseTimer.current = setTimeout(() => {
      setMobileOpen(false);
      setMobileClosing(false);
      setMobileServicesOpen(false);
      setMobileAboutOpen(false);
      setMobileTradeOpen(false);
      setMobileFinanceOpen(false);
      mobileCloseTimer.current = null;
    }, 150);
  }, [mobileOpen, mobileClosing]);

  useEffect(() => {
    return () => {
      if (mobileCloseTimer.current) clearTimeout(mobileCloseTimer.current);
    };
  }, []);

  useEffect(() => {
    ensureGhlPreconnect();
  }, []);

  const openServices = () => {
    if (servicesCloseTimer.current) {
      clearTimeout(servicesCloseTimer.current);
      servicesCloseTimer.current = null;
    }
    setServicesOpen(true);
  };
  const scheduleCloseServices = () => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    servicesCloseTimer.current = setTimeout(() => setServicesOpen(false), 180);
  };
  const openAbout = () => {
    if (aboutCloseTimer.current) {
      clearTimeout(aboutCloseTimer.current);
      aboutCloseTimer.current = null;
    }
    setAboutOpen(true);
  };
  const scheduleCloseAbout = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    aboutCloseTimer.current = setTimeout(() => setAboutOpen(false), 180);
  };
  const openTrade = () => {
    if (tradeCloseTimer.current) {
      clearTimeout(tradeCloseTimer.current);
      tradeCloseTimer.current = null;
    }
    setTradeOpen(true);
  };
  const scheduleCloseTrade = () => {
    if (tradeCloseTimer.current) clearTimeout(tradeCloseTimer.current);
    tradeCloseTimer.current = setTimeout(() => setTradeOpen(false), 180);
  };
  const openFinance = () => {
    if (financeCloseTimer.current) {
      clearTimeout(financeCloseTimer.current);
      financeCloseTimer.current = null;
    }
    setFinanceOpen(true);
  };
  const scheduleCloseFinance = () => {
    if (financeCloseTimer.current) clearTimeout(financeCloseTimer.current);
    financeCloseTimer.current = setTimeout(() => setFinanceOpen(false), 180);
  };

  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > 8;
      setScrolled((prev) => (prev === next ? prev : next));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (showBottomStickyBar) {
      document.documentElement.dataset.mobileStickyCta = "true";
    } else {
      delete document.documentElement.dataset.mobileStickyCta;
    }
    return () => {
      delete document.documentElement.dataset.mobileStickyCta;
    };
  }, [showBottomStickyBar]);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeMobileDrawer();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileOpen, closeMobileDrawer]);

  return (
    <>
      <div className="site-sticky-chrome">
        <TopUtilityBar />
        <header
          className={cn(
            "relative z-30 w-full max-lg:transition-none lg:transition-[background-color,box-shadow,border-color] lg:duration-200",
            scrolled
              ? "bg-background/95 backdrop-blur border-b shadow-soft"
              : "bg-background/80 backdrop-blur-sm",
          )}
        >
        <div className="container-page flex items-center justify-between gap-3 xl:gap-6 py-3 lg:py-5">
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="CRS Energy home">
            <img src={logo} alt="CRS Energy" width={900} height={345} className="h-14 md:h-16 w-auto" decoding="async" />
          </Link>


          <nav className={cn("hidden lg:flex items-center gap-1", isLp && "ml-auto")} aria-label="Primary">
            {isLp ? (
              <>
                <Link
                  to="/"
                  className="px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                >
                  Home
                </Link>
                {lpNavAnchors.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    className="px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                  >
                    {l.label}
                  </a>
                ))}
              </>
            ) : (
              <>
                {/* Home */}
                <Link
                  to="/"
                  className="px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                  activeProps={{ className: "px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  activeOptions={{ exact: true }}
                >
                  Home
                </Link>

                {/* About dropdown */}
                <div
                  className="relative"
                  onMouseEnter={openAbout}
                  onMouseLeave={scheduleCloseAbout}
                >
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    About
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </Link>
                  {aboutOpen && (
                    <div
                      role="menu"
                      onMouseEnter={openAbout}
                      onMouseLeave={scheduleCloseAbout}
                      className="absolute left-0 top-full pt-2 z-50 min-w-[15rem]"
                    >
                      <div className="rounded-xl border bg-popover shadow-lift p-2">
                        {ABOUT_LINKS.map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            onClick={() => setAboutOpen(false)}
                            className="block rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary hover:text-accent transition-colors"
                          >
                            {l.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Services mega menu */}
                <div
                  className="relative"
                  onMouseEnter={openServices}
                  onMouseLeave={scheduleCloseServices}
                >
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    Services
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </Link>
                  {servicesOpen && (
                    <MegaMenu
                      onItemClick={() => setServicesOpen(false)}
                      onMouseEnter={openServices}
                      onMouseLeave={scheduleCloseServices}
                    />
                  )}
                </div>

                {showTradeMenu ? (
                <div
                  className="relative"
                  onMouseEnter={openTrade}
                  onMouseLeave={scheduleCloseTrade}
                >
                  <Link
                    to="/for-trade"
                    className="inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    Partnerships
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </Link>
                  {tradeOpen && (
                    <TradeMegaMenu
                      onItemClick={() => setTradeOpen(false)}
                      onMouseEnter={openTrade}
                      onMouseLeave={scheduleCloseTrade}
                    />
                  )}
                </div>
                ) : (
                  <Link
                    to="/for-trade"
                    className="px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    Partnerships
                  </Link>
                )}




                {/* Financing dropdown */}
                <div
                  className="relative"
                  onMouseEnter={openFinance}
                  onMouseLeave={scheduleCloseFinance}
                >
                  <Link
                    to="/financing"
                    className="inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "inline-flex items-center gap-1 px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    Financing
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </Link>
                  {financeOpen && (
                    <div
                      role="menu"
                      onMouseEnter={openFinance}
                      onMouseLeave={scheduleCloseFinance}
                      className="absolute left-0 top-full pt-2 z-50 min-w-[15rem]"
                    >
                      <div className="rounded-xl border bg-popover shadow-lift p-2">
                        {financeNavLinks.map((l) => (
                          <FinanceNavItem
                            key={l.label}
                            label={l.label}
                            href={l.href}
                            external={l.external}
                            onClick={() => setFinanceOpen(false)}
                            className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary hover:text-accent transition-colors"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {NAV_LINKS.slice(1).map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-foreground hover:text-accent transition-colors whitespace-nowrap"
                    activeProps={{ className: "px-2 xl:px-3 py-2 text-sm xl:text-base font-semibold text-accent whitespace-nowrap" }}
                  >
                    {l.label}
                  </Link>
                ))}
              </>
            )}
          </nav>


          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <BookACallButton label="Book A Call" />
            <a
              href={ctaPhone.tel}
              aria-label={`Call ${ctaPhone.label}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors shadow-cta"
            >
              <Phone className="h-4 w-4" aria-hidden />
              Call
            </a>
          </div>



          <button
            type="button"
            className="lg:hidden p-3 rounded-md text-foreground hover:bg-secondary min-h-11 min-w-11 inline-flex items-center justify-center"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
        
        <ReviewsBar />
        </header>

        {showLeadMagnetSticky && <HomeLeadMagnetStickyBar />}
      </div>

      {(mobileOpen || mobileClosing) && (
        <div className="fixed inset-0 z-50 lg:hidden site-fixed-layer">
          <div
            className={cn(
              "absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity duration-150",
              mobileClosing ? "opacity-0" : "opacity-100",
            )}
            onClick={closeMobileDrawer}
            aria-hidden
          />
          <div
            className={cn(
              "absolute right-0 top-0 h-full w-[min(22rem,90vw)] bg-background shadow-xl flex flex-col transition-transform duration-150 ease-out",
              mobileClosing ? "translate-x-full" : "translate-x-0",
            )}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <img src={logo} alt="CRS Energy" width={900} height={345} className="h-14 w-auto" decoding="async" />
              <button
                type="button"
                className="p-3 rounded-md hover:bg-secondary min-h-11 min-w-11 inline-flex items-center justify-center"
                aria-label="Close menu"
                onClick={closeMobileDrawer}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav
              className={cn("flex-1 overflow-y-auto p-4 space-y-1", isHome && "pb-24")}
              aria-label="Mobile"
            >
              {isLp ? (
                <>
                  <Link
                    to="/"
                    onClick={closeMobileDrawer}
                    className="block px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                  >
                    Home
                  </Link>
                  {lpNavAnchors.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={closeMobileDrawer}
                      className="block px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                    >
                      {l.label}
                    </a>
                  ))}
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={closeMobileDrawer}
                    className="block px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                  >
                    Home
                  </Link>
                  <div className="flex w-full items-center rounded-lg hover:bg-secondary">
                    <Link
                      to="/about"
                      onClick={closeMobileDrawer}
                      className="flex-1 px-4 py-3 font-medium"
                    >
                      About
                    </Link>
                    <button
                      type="button"
                      className="px-3 py-3 rounded-md hover:bg-secondary/80 min-h-11 min-w-11 inline-flex items-center justify-center shrink-0"
                      onClick={() => setMobileAboutOpen((o) => !o)}
                      aria-expanded={mobileAboutOpen}
                      aria-label={mobileAboutOpen ? "Collapse About menu" : "Expand About menu"}
                    >
                      <ChevronDown
                        className={cn("h-4 w-4 transition-transform", mobileAboutOpen && "rotate-180")}
                        aria-hidden
                      />
                    </button>
                  </div>
                  {mobileAboutOpen && (
                    <div className="pl-4 space-y-0.5">
                      {ABOUT_LINKS.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          onClick={closeMobileDrawer}
                          className="block px-4 py-2 text-sm hover:bg-secondary rounded-md"
                        >
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  )}
                  <div className="flex w-full items-center rounded-lg hover:bg-secondary">
                    <Link
                      to="/services"
                      onClick={closeMobileDrawer}
                      className="flex-1 px-4 py-3 font-medium"
                    >
                      Services
                    </Link>
                    <button
                      type="button"
                      className="px-3 py-3 rounded-md hover:bg-secondary/80 min-h-11 min-w-11 inline-flex items-center justify-center shrink-0"
                      onClick={() => setMobileServicesOpen((o) => !o)}
                      aria-expanded={mobileServicesOpen}
                      aria-label={mobileServicesOpen ? "Collapse Services menu" : "Expand Services menu"}
                    >
                      <ChevronDown
                        className={cn("h-4 w-4 transition-transform", mobileServicesOpen && "rotate-180")}
                        aria-hidden
                      />
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className="pl-4 space-y-0.5">
                      <Link
                        to="/services"
                        onClick={closeMobileDrawer}
                        className="block px-4 py-2 text-sm font-semibold text-accent hover:bg-secondary rounded-md"
                      >
                        All services →
                      </Link>
                      {MEGA_MENU_SPOTLIGHTS.map((spotlight) => (
                        <Link
                          key={spotlight.to}
                          to={spotlight.to}
                          onClick={closeMobileDrawer}
                          className="block px-4 py-2 text-sm font-semibold text-accent hover:bg-secondary rounded-md"
                        >
                          {spotlight.label} →
                        </Link>
                      ))}
                      {(Object.keys(SERVICES_BY_CATEGORY) as Array<keyof typeof SERVICES_BY_CATEGORY>).map(
                        (cat) =>
                          SERVICES_BY_CATEGORY[cat].map((s) => {
                            const lpOverride = LP_SLUG_OVERRIDES[s.slug];
                            const linkProps = lpOverride
                              ? ({ to: lpOverride } as const)
                              : ({ to: "/services/$slug", params: { slug: s.slug } } as const);
                            return (
                              <Link
                                key={s.slug}
                                {...linkProps}
                                onClick={closeMobileDrawer}
                                className="block px-4 py-2 text-sm hover:bg-secondary rounded-md"
                              >
                                {s.shortName}
                              </Link>
                            );
                          })
                      )}
                    </div>
                  )}
                  {showTradeMenu ? (
                  <>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                    onClick={() => setMobileTradeOpen((o) => !o)}
                    aria-expanded={mobileTradeOpen}
                  >
                    Partnerships
                    <ChevronDown
                      className={cn("h-4 w-4 transition-transform", mobileTradeOpen && "rotate-180")}
                      aria-hidden
                    />
                  </button>
                  {mobileTradeOpen && (
                    <div className="pl-4 space-y-0.5">
                      <Link
                        to="/for-trade"
                        onClick={closeMobileDrawer}
                        className="block px-4 py-2 text-sm font-semibold text-accent hover:bg-secondary rounded-md"
                      >
                        Partnerships Hub →
                      </Link>
                      {TRADE_PROGRAMMES.map((l) => (
                        <Link
                          key={l.href}
                          to={l.href}
                          onClick={closeMobileDrawer}
                          className="block px-4 py-2 text-sm hover:bg-secondary rounded-md"
                        >
                          {l.label}
                        </Link>
                      ))}
                      {TRADE_RESOURCES.map((l) => (
                        <Link
                          key={l.href}
                          to={l.href}
                          onClick={closeMobileDrawer}
                          className="block px-4 py-2 text-sm hover:bg-secondary rounded-md"
                        >
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  )}
                  </>
                  ) : (
                    <Link
                      to="/for-trade"
                      onClick={closeMobileDrawer}
                      className="block px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                    >
                      Partnerships
                    </Link>
                  )}

                  <div className="flex w-full items-center rounded-lg hover:bg-secondary">
                    <Link
                      to="/financing"
                      onClick={closeMobileDrawer}
                      className="flex-1 px-4 py-3 font-medium"
                    >
                      Financing
                    </Link>
                    <button
                      type="button"
                      className="px-3 py-3 rounded-md hover:bg-secondary/80 min-h-11 min-w-11 inline-flex items-center justify-center shrink-0"
                      onClick={() => setMobileFinanceOpen((o) => !o)}
                      aria-expanded={mobileFinanceOpen}
                      aria-label={mobileFinanceOpen ? "Collapse Financing menu" : "Expand Financing menu"}
                    >
                      <ChevronDown
                        className={cn("h-4 w-4 transition-transform", mobileFinanceOpen && "rotate-180")}
                        aria-hidden
                      />
                    </button>
                  </div>
                  {mobileFinanceOpen && (
                    <div className="pl-4 space-y-0.5">
                      {financeNavLinks.map((l) => (
                        <FinanceNavItem
                          key={l.label}
                          label={l.label}
                          href={l.href}
                          external={l.external}
                          onClick={closeMobileDrawer}
                          className="flex items-center justify-between gap-2 px-4 py-2 text-sm hover:bg-secondary rounded-md"
                        />
                      ))}
                    </div>
                  )}

                  {NAV_LINKS.slice(1).map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={closeMobileDrawer}
                      className="block px-4 py-3 rounded-lg font-medium hover:bg-secondary"
                    >
                      {l.label}
                    </Link>
                  ))}
                </>
              )}
            </nav>

            <div className="p-4 border-t space-y-2">
              <BookACallButton
                className="flex w-full bg-accent text-accent-foreground border-transparent hover:bg-accent/90 py-3"
              />
              <a
                href={ctaPhone.tel}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-primary text-primary-foreground font-semibold"
              >
                <Phone className="h-4 w-4" />
                {ctaPhone.label}
              </a>

              <EmailContactLink className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full border font-medium">
                <span className="inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" aria-hidden />
                  {SITE.email}
                </span>
              </EmailContactLink>
            </div>

          </div>
        </div>
      )}

      {showBottomStickyBar && <MobileStickyCtaBar position="bottom" />}
    </>
  );
}

function LpMobileCtaBar() {
  const phone = usePagePhone();
  const [bookOpen, setBookOpen] = useState(false);
  return (
    <div className="mobile-sticky-cta-bar lg:hidden bg-background/95 backdrop-blur border-t shadow-lift">
      <div className="grid grid-cols-3 gap-2 p-2">
        <a
          href={phone.tel}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-3 text-sm font-semibold shadow-cta active:scale-[0.98] transition"
        >
          <Phone className="h-4 w-4" aria-hidden />
          Call
        </a>
        <button
          type="button"
          onClick={() => setBookOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-3 text-sm font-semibold shadow-soft active:scale-[0.98] transition"
        >
          <CalendarClock className="h-4 w-4" aria-hidden />
          Book
        </button>
        <a
          href="#contact"
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-input bg-background text-foreground px-3 py-3 text-sm font-semibold active:scale-[0.98] transition"
        >
          <Mail className="h-4 w-4" aria-hidden />
          Contact
        </a>
      </div>
      <BookACallDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}


